// src/App.js
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import './App.css';
import Auth from "./Auth/Index";
import DashboardLayout from "./Components/DashboardLayout";
import Dashboard from "./Dashboard/Dashboard";
import PrivateRoute from "./Components/PrivateRoute";
import PublicRoute from "./Components/PublicRoute";
import NotFoundPage from "./Components/NotFoundPage";
import Settings from "./Settings/Settings";
import UserManagement from "./Settings/UserManagement";
import KnowledgeBase from "./KnowledgeBase/KnowledgeBase";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import MaintenancePage from "./Components/MaintenancePage";
import HealthCheckMonitor from "./Components/HealthCheckMonitor";
import settings from "./services/config/settings";
import Questionnaire from "./Dashboard/ComplianceReady/Index";
import SummaryPage from "./Dashboard/ComplianceReady/Summary";
import OrganizationQuestions from "./Dashboard/ThirdpartyExternalExposure/OrganizationQuestions";
import VendorAssessmentView from "./Dashboard/ThirdpartyExternalExposure/VendorAssessmentView";
import DashboardV2 from "./Dashboard/DashboardV2";
import DomainCompliance from "./Dashboard/DomainCompliance";
import BankSignup from "./Auth/BankSignup";
import BankUserDashboard from "./Dashboard/BankUserDashboard";
import BankUserDomains from "./Dashboard/ComplianceReady/BankUserDomains";
import BankUserDomainControls from "./Dashboard/ComplianceReady/BankUserDomainControls";
import BankUserControlQuestions from "./Dashboard/ComplianceReady/BankUserControlQuestions";
import PhishingTraining from "./Dashboard/AddOns/PhishingTraining";
import IntegrationCloudPage from "./Dashboard/IntegrationCloud/IntegrationCloudPage";
import CloudOrgScorePage from "./Dashboard/IntegrationCloud/CloudOrgScorePage";
import CloudServiceDetails from "./Dashboard/IntegrationCloud/CloudServiceDetails";
import RiskPortal from "./RiskPortal/RiskPortal";
import RiskOwnerView from "./RiskPortal/RiskOwnerView";
import RiskPortalAuth from "./RiskPortal/RiskPortalAuth";
import RiskPortalForgotPassword from "./RiskPortal/RiskPortalForgotPassword";
import RiskPortalResetPassword from "./RiskPortal/RiskPortalResetPassword";
import AuditPortal from "./AuditPortal/AuditPortal";
import InternalAuditLogin from "./Dashboard/Dashboard_InternalAudit/InternalAuditLogin";
import InternalAuditSignup from "./Dashboard/Dashboard_InternalAudit/InternalAuditSignup";
import InternalAuditForgotPassword from "./AuditPortal/InternalAuditForgotPassword";
import InternalAuditResetPassword from "./AuditPortal/InternalAuditResetPassword";
import BrandMentions from "./Dashboard/BrandProtection/BrandMentions";
import AuditPortalV2 from "./AuditPortal/AuditPortalV2";


const AuditPortalGuard = () => {
  const profile = JSON.parse(localStorage.getItem('user_profile') || '{}');
  const hasDashboardAuth = !!profile?.emailId && (!!sessionStorage.getItem('access_token') || !!localStorage.getItem('access_token'));
  const portalUser = JSON.parse(localStorage.getItem('audit_portal_user') || 'null');
  const hasPortalToken = !!localStorage.getItem('audit_portal_token');
  const isPortalLogin = !!portalUser && hasPortalToken && !hasDashboardAuth;
  if (!hasDashboardAuth && !isPortalLogin) {
    return <Navigate to="/audit-portal/login" replace />;
  }
  return <AuditPortalV2 />;
};

const POLL_INTERVAL = 30000;
const API_BASE = settings.api.url;

const MAINTENANCE_KEY = 'nmt_maintenance_mode';

function App() {
  const isDevPreview = process.env.REACT_APP_ENV !== 'prod'
    && new URLSearchParams(window.location.search).get('maintenance') === 'true';

  const [underMaintenance, setUnderMaintenance] = useState(() => {
    if (isDevPreview) return true;
    return localStorage.getItem(MAINTENANCE_KEY) === '1';
  });
  const pollRef = useRef(null);

  const enterMaintenance = () => {
    // Sign the user out behind the scenes so when the deploy completes and the
    // maintenance page is dismissed, they land on the login screen.
    sessionStorage.clear();
    localStorage.setItem(MAINTENANCE_KEY, '1');
    setUnderMaintenance(true);
  };

  const exitMaintenance = () => {
    localStorage.removeItem(MAINTENANCE_KEY);
    setUnderMaintenance(false);
  };

  useEffect(() => {
    window.addEventListener('maintenance_mode', enterMaintenance);
    return () => window.removeEventListener('maintenance_mode', enterMaintenance);
  }, []);

  // When server is down, poll every 30s; clear maintenance once it responds.
  // Also fires one immediate probe so a stale localStorage flag (e.g. from
  // a previous deploy or a transient network blip) clears in under a second
  // instead of making the user stare at the maintenance page for 30s.
  useEffect(() => {
    if (!underMaintenance || isDevPreview) return;

    const checkServer = async () => {
      try {
        await axios.get(API_BASE, { timeout: 5000 });
        exitMaintenance();
      } catch (err) {
        const status = err?.response?.status;
        // Gateway still down for deploy — keep waiting
        if (status === 502 || status === 503 || status === 504) return;
        // No response at all (network error / CORS) — gateway still down
        if (!err.response) return;
        // Any other status means the server is back up
        exitMaintenance();
      }
    };

    checkServer();
    pollRef.current = setInterval(checkServer, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [underMaintenance, isDevPreview]);

  const DashboardSwitch = () => {
    const profile = JSON.parse(localStorage.getItem('user_profile') || '{}');
    const userRoles = profile?.user_roles || [];
    const isBankUser = userRoles.includes('BankUser');
    const isBankAudit = profile?.bank_audit_valid === true;

    if (isBankUser) return <BankUserDashboard />;
    if (isBankAudit) return <DashboardV2 />;
    return <Dashboard />;
  };

  if (underMaintenance) {
    return <MaintenancePage />;
  }
  return (
    <Router>
      <HealthCheckMonitor />
      <div className="scroller-container">
        <Routes>
          {/* Auth routes moved below other specific routes */}
          <Route
            path="/dashboardv2"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <DashboardV2 />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/domain-compliance"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <DomainCompliance />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <DashboardSwitch />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <Settings />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/brand-protection"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <BrandMentions />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <UserManagement />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <UserManagement />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/knowledge_base/"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <KnowledgeBase />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route path="/questionnaire/:framework" element={
            <PrivateRoute>
              <DashboardLayout>
                <Questionnaire />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/bank-user/framework/:frameworkId/domains" element={
            <PrivateRoute>
              <DashboardLayout hideBreadcrumb={true}>
                <BankUserDomains />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/bank-user/framework/:frameworkId/domain/:domainId/controls" element={
            <PrivateRoute>
              <DashboardLayout hideBreadcrumb={true}>
                <BankUserDomainControls />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/bank-user/framework/:frameworkId/domain/:domainId/control/:controlId/questions" element={
            <PrivateRoute>
              <DashboardLayout hideBreadcrumb={true}>
                <BankUserControlQuestions />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/summary/:frameworkId/:frameworkName" element={
            <PrivateRoute>
              <DashboardLayout>
                <SummaryPage />
              </DashboardLayout>
            </PrivateRoute>
          } />
          <Route path="/organization-questions" element={
            <PrivateRoute>
              <DashboardLayout>
                <OrganizationQuestions />
              </DashboardLayout>
            </PrivateRoute>
          } />

          <Route path="/integration-cloud" element={
            <PrivateRoute>
              <DashboardLayout>
                <IntegrationCloudPage wrapper={true} />
              </DashboardLayout>
            </PrivateRoute>
          } />

          <Route path="/cloud-compliance-details" element={
            <PrivateRoute>
              <DashboardLayout>
                <CloudOrgScorePage />
              </DashboardLayout>
            </PrivateRoute>
          } />

          <Route path="/cloud-service-details/:controlId" element={
            <PrivateRoute>
              <DashboardLayout>
                <CloudServiceDetails />
              </DashboardLayout>
            </PrivateRoute>
          } />

          {/* Public Vendor Assessment Route - No Auth Required */}
          <Route path="/vendor/assessment/:access_token" element={<VendorAssessmentView />} />

          {/* Public Bank Signup Route - No Auth Required */}
          <Route path="/bank-signup" element={<BankSignup />} />

          {/* Assessment Responses View Route - Auth Required */}
          <Route path="/vendor/assessment/:assessment_id/responses" element={
            <PrivateRoute>
              <DashboardLayout>
                <VendorAssessmentView />
              </DashboardLayout>
            </PrivateRoute>
          } />

          <Route path="/phishing-training" element={<PhishingTraining />} />

          {/* Risk Portal Routes - No Auth Required (portal has its own auth) */}
          <Route path="/risk-portal" element={<RiskPortal />} />
          <Route path="/risk-portal/login" element={<RiskPortalAuth />} />
          <Route path="/risk-portal/register" element={<RiskPortalAuth />} />
          <Route path="/risk-portal/forgot-password" element={<RiskPortalForgotPassword />} />
          <Route path="/risk-portal/reset-password" element={<RiskPortalResetPassword />} />
          <Route path="/risk-owner/:token" element={<RiskOwnerView />} />

          {/* Audit Portal Routes - guard checks dashboard session or portal token */}
          <Route path="/audit-portal" element={<AuditPortalGuard />} />
          <Route path="/audit-portal/login" element={<InternalAuditLogin />} />
          <Route path="/audit-portal/signup" element={<InternalAuditSignup />} />
          <Route path="/audit-portal/register" element={<InternalAuditSignup />} />
          <Route path="/audit-portal/forgot-password" element={<InternalAuditForgotPassword />} />
          <Route path="/audit-portal/reset-password" element={<InternalAuditResetPassword />} />

          {/* Auth routes moved here */}
          <Route
            path="/*"
            element={
              <PublicRoute>
                <Auth />
              </PublicRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
